// components/Sender/SenderTemplatesTab.js
import React, { useState } from "react";
import { SenderApi, TOKEN_KEY } from "./SenderApi";
import { ImportJsonDialog } from "./ImportJsonDialog";
import { TemplateEditor } from "./TemplateEditor";
import { TemplatePreview } from "./TemplatePreview";

function _fmtDateTime(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _computeFullName(acc) {
  if (!acc) return "";
  const display = (acc.display_name || "").trim();
  if (display) return display;
  const first = (acc.first_name || "").trim();
  const last = (acc.last_name || "").trim();
  return [first, last].filter(Boolean).join(" ").trim();
}

export function SenderTemplatesTab({
  templates, setTemplates, segments, workers, campaigns, runtimes,
  emailAccounts,
  workersError, onRefreshWorkers, onLaunched, add,
}) {
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const accountById = (() => {
    const m = {};
    (emailAccounts || []).forEach((a) => { m[a.id] = a; });
    return m;
  })();

  const workerByName = (() => {
    const m = {};
    (workers || []).forEach((w) => { m[w._name || w.name] = w; });
    return m;
  })();

  const refresh = async () => {
    try {
      const list = await SenderApi.listTemplates();
      setTemplates(list || []);
    } catch (e) {
      add?.("Не удалось обновить шаблоны: " + e.message, "error");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === templates.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(templates.map((t) => t.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const deleteOne = async (t) => {
    if (!window.confirm(
      `Удалить шаблон «${t.name}»?\nБлоки шаблона тоже удалятся.`)) return;
    try {
      await SenderApi.deleteTemplate(t.id);
      setTemplates(templates.filter((x) => x.id !== t.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
      if (expandedId === t.id) setExpandedId(null);
      add?.("🗑 Шаблон удалён", "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(
      `Удалить ${ids.length} шаблон(ов)?\nИх блоки тоже удалятся.`)) return;
    setBusy(true);
    try {
      const res = await SenderApi.bulkDeleteTemplates(ids);
      const deleted = new Set(res.deleted || []);
      setTemplates(templates.filter((t) => !deleted.has(t.id)));
      setSelectedIds(new Set());
      if (deleted.has(expandedId)) setExpandedId(null);
      add?.(`🗑 Удалено шаблонов: ${deleted.size}`, "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (tpl) => {
    const name = window.prompt("Название копии:", `${tpl.name} (копия)`);
    if (!name) return;
    try {
      const payload = {
        name,
        segment_blocks: (tpl.segment_blocks || []).map((b) => ({
          segment_id: b.segment_id,
          mode: b.mode,
          limit: b.limit,
          limit_mode: b.limit_mode,
          test_mode: b.test_mode,
          cf_selection: b.cf_selection,
          cf_failed_action: b.cf_failed_action,
          fetch_mode: b.fetch_mode || "sequential",
          runtime_defaults: b.runtime_defaults || {},
          workers: (b.workers || []).map((w) => ({ ...w })),
        })),
        total_limit: tpl.total_limit,
        pause_min: tpl.pause_min,
        pause_max: tpl.pause_max,
        test_run: tpl.test_run,
        test_fill_cf: tpl.test_fill_cf,
      };
      const created = await SenderApi.createTemplate(payload);
      setTemplates([created, ...templates]);
      add?.(`📋 Скопировано как «${created.name}»`, "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  // ─── сборка snapshot ───
  const buildSnapshot = (tpl) => {
    const token = (() => {
      try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
    })();

    const usedSegIds = new Set(
      (tpl.segment_blocks || []).map((b) => b.segment_id).filter(Boolean)
    );
    const segments_map = {};
    segments.forEach((s) => {
      if (usedSegIds.has(s.id)) {
        segments_map[s.id] = {
          id: s.id, name: s.name, filters: s.filters || {},
        };
      }
    });

    const usedWorkerNames = new Set();
    (tpl.segment_blocks || []).forEach((b) => {
      (b.workers || []).forEach((w) => usedWorkerNames.add(w.worker_name));
    });

    // ─── worker_accounts ───
    // Полный аккаунт + транспортный конфиг (channels)
    
    const worker_accounts = {};
    usedWorkerNames.forEach((wname) => {
      const w = workerByName[wname];
      if (!w) return;
      const acc = accountById[w._account_id];
      if (!acc) return;
      const first = (acc.first_name || "").trim();
      const last = (acc.last_name || "").trim();
      const display = (acc.display_name || "").trim();
      worker_accounts[wname] = {
        id: acc.id,
        worker_name: wname,
        worker_type: w._type || "email",
        email: acc.email || "",
        first_name: first,
        last_name: last,
        display_name: display,
        full_name: (display || `${first} ${last}`).trim(),
        phone_number: acc.phone_number || "",
        provider: acc.provider || "",
        account_config: acc.config || {},
        worker_config: w.config || {},
      };
    });

    // ─── runtime_configs ───
    const runtime_configs = {};
    (runtimes || []).forEach((rt) => {
      runtime_configs[rt.id] = rt.config || {};
    });

    return {
      template_id: tpl.id,
      template_name: tpl.name,
      total_limit: tpl.total_limit || 0,
      pause_min: tpl.pause_min || 0,
      pause_max: tpl.pause_max || 0,
      test_run: !!tpl.test_run,
      test_fill_cf: tpl.test_fill_cf !== false,
      segment_blocks: (tpl.segment_blocks || []).map((b) => ({
        segment_id: b.segment_id,
        mode: b.mode,
        limit: b.limit || 0,
        limit_mode: b.limit_mode || "abs",
        cf_selection: b.cf_selection || "random",
        cf_failed_action: b.cf_failed_action || "send_email",
        runtime_defaults: b.runtime_defaults || {},
        workers: (b.workers || []).map((w) => {
          const wObj = workerByName[w.worker_name];
          const realType = wObj?._type;
          const effectiveChannel = realType || w.channel || "EMAIL";
          return {
            worker_name: w.worker_name,
            percent: w.percent || 100,
            limit: w.limit || 0,
            limit_mode: w.limit_mode || "abs",
            channel: effectiveChannel,
            runtime_id: w.runtime_id || null,
          };
        }),
      })),
      segments_map,
      worker_accounts,
      runtime_configs,
      api_config: {
        base: SenderApi.globalOutreachBase,
        token,
      },
    };
  };

  const [exportingId, setExportingId] = useState(null);

  const exportTemplate = async (template) => {
    setExportingId(template.id);
    try {
      const data = await SenderApi.exportWorkerView(template.id);
      const url = URL.createObjectURL(new Blob(
        [JSON.stringify(data, null, 2)],
        { type: "application/json;charset=utf-8" }
      ));
      const link = document.createElement("a");
      link.href = url;
      link.download = `template-${template.id}-worker-view.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      add?.("Ошибка экспорта: " + e.message, "error");
    } finally {
      setExportingId(null);
    }
  };

  const createRun = async (tpl) => {
    const name = `${tpl.name} — ${_fmtDateTime(new Date())}`;
    try {
      const snapshot = buildSnapshot(tpl);
      const lr = await SenderApi.createLocalRun({
        template_id: tpl.id,
        template_name: name,
        snapshot,
        overrides: {
          total_limit: tpl.total_limit || 0,
          pause_min: tpl.pause_min || 0,
          pause_max: tpl.pause_max || 0,
          test_run: !!tpl.test_run,
          test_fill_cf: tpl.test_fill_cf !== false,
        },
      });
      add?.(
        `📋 Рассылка «${name}» создана. Открой 🚀 Запуски → ▶`,
        "success");
      onLaunched?.(lr);
    } catch (e) {
      add?.(
        "Ошибка создания: " + e.message +
        " (локальный бекенд 127.0.0.1:17873 не отвечает?)",
        "error");
    }
  };

  const onEditorSave = (saved) => {
    const exists = templates.some((t) => t.id === saved.id);
    if (exists) {
      setTemplates(templates.map((t) => (t.id === saved.id ? saved : t)));
    } else {
      setTemplates([saved, ...templates]);
    }
    setEditing(null);
  };

  const selectedCount = selectedIds.size;
  const allSelected = selectedCount > 0 && selectedCount === templates.length;

  return (
    <div>
      <div className="section-card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h3 style={{ margin: 0 }}>
              📦 Шаблоны запусков
              {selectedCount > 0 && (
                <span style={{ fontSize: 13, color: "#dc2626",
                                fontWeight: 400, marginLeft: 10 }}>
                  · выбрано {selectedCount}
                </span>
              )}
            </h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Всего: {templates.length} · хранятся на глобальном бекенде
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {selectedCount > 0 ? (
              <>
                <button className="btn-cancel" onClick={clearSelection}>
                  Снять выбор
                </button>
                <button className="btn-cancel" disabled={busy}
                        onClick={bulkDelete}
                        style={{ color: "#dc2626", borderColor: "#fecaca" }}>
                  🗑 Удалить выбранные ({selectedCount})
                </button>
              </>
            ) : (
              <>
                <button className="btn-cancel" onClick={refresh}>🔄 Обновить</button>
                <button className="btn-cancel"
                        onClick={() => setImportOpen(true)}>
                  📥 Импорт
                </button>
                <button className="btn-primary"
                        onClick={() => setEditing({ template: null })}>
                  ➕ Новый шаблон
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="section-card">
        {templates.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            <div style={{ marginBottom: 12 }}>Пока нет ни одного шаблона.</div>
            <button className="btn-primary"
                    onClick={() => setEditing({ template: null })}>
              ➕ Создать первый шаблон
            </button>
          </div>
        ) : (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", background: "#f9fafb",
              borderRadius: 8, marginBottom: 10, fontSize: 12,
            }}>
              <input type="checkbox" checked={allSelected}
                     onChange={toggleSelectAll} />
              <span style={{ color: "#6b7280" }}>
                {allSelected ? "Снять выбор со всех" : "Выбрать все"}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {templates.map((t) => {
                const workerNames = new Set();
                (t.segment_blocks || []).forEach((b) => {
                  (b.workers || []).forEach((w) => workerNames.add(w.worker_name));
                });
                const isExpanded = expandedId === t.id;
                const isSelected = selectedIds.has(t.id);

                return (
                  <div key={t.id}
                       style={{
                         border: `1px solid ${isSelected ? "#93c5fd" : "#e5e7eb"}`,
                         background: isSelected ? "#eff6ff" : "#fff",
                         borderRadius: 10, padding: 12,
                       }}>
                    <div style={{ display: "flex", alignItems: "center",
                                  gap: 10 }}>
                      <input type="checkbox"
                             checked={isSelected}
                             onChange={() => toggleSelect(t.id)} />
                      <div style={{ flex: 1, cursor: "pointer" }}
                           onClick={() => setExpandedId(isExpanded ? null : t.id)}>
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          {isExpanded ? "▾" : "▸"} {t.name}
                          {t.test_run && (
                            <span style={{ color: "#b45309",
                                            marginLeft: 6, fontWeight: 400 }}>
                              🧪
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          📦 {(t.segment_blocks || []).length} блоков ·{" "}
                          👥 {workerNames.size} воркеров ·{" "}
                          🎯 {t.total_limit || "∞"} ·{" "}
                          ⏸ {(t.pause_min || 0).toFixed?.(1) ?? 0}–
                          {(t.pause_max || 0).toFixed?.(1) ?? 0} сек
                          {t.created_at && (
                            <span style={{ color: "#9ca3af" }}>
                              {" "}· {new Date(t.created_at).toLocaleString("ru-RU")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap",
                                    justifyContent: "flex-end" }}>
                        <button className="btn-cancel"
                                style={{ padding: "6px 10px", fontSize: 12 }}
                                onClick={() => setEditing({ template: t })}>
                          ✏️
                        </button>
                        <button className="btn-primary"
                                style={{ padding: "6px 12px", fontSize: 12,
                                         background: "#16a34a",
                                         borderColor: "#16a34a" }}
                                onClick={() => createRun(t)}
                                title="Создать рассылку (запуск — в 🚀 Запуски)">
                          📋 Создать
                        </button>
                        <button className="btn-cancel"
                                style={{ padding: "6px 10px", fontSize: 12 }}
                                onClick={() => duplicate(t)}>📋</button>
                        <button className="btn-cancel"
                                style={{ padding: "6px 10px", fontSize: 12 }}
                                disabled={exportingId !== null}
                                onClick={() => exportTemplate(t)}>
                          {exportingId === t.id ? "Экспорт…" : "Экспорт JSON"}
                        </button>
                        <button className="btn-cancel"
                                style={{ padding: "6px 10px", fontSize: 12,
                                         color: "#dc2626" }}
                                onClick={() => deleteOne(t)}>🗑</button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: 12, paddingTop: 12,
                                    borderTop: "1px solid #f3f4f6" }}>
                        <TemplatePreview template={t} segments={segments} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && (
        <TemplateEditor
          template={editing.template}
          segments={segments}
          workers={workers}
          campaigns={campaigns}
          runtimes={runtimes}
          onSave={onEditorSave}
          onCancel={() => setEditing(null)}
          onRefreshWorkers={onRefreshWorkers}
          add={add}
        />
      )}

      {importOpen && (
        <ImportJsonDialog
          kind="templates"
          add={add}
          onClose={() => setImportOpen(false)}
          onImported={refresh}
        />
      )}
    </div>
  );
}